import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, FloppyDisk, Copy, Rocket, Money, Globe, ClockCounterClockwise } from "@phosphor-icons/react";

const dollars = (c) => (Number(c || 0) / 100).toFixed(2);
const toCents = (v) => Math.max(0, Math.round(parseFloat(v || 0) * 100));

function PriceInput({ label, value, onChange }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex items-center rounded-xl border border-white/10 bg-background px-2">
        <span className="text-sm text-muted-foreground">$</span>
        <input type="number" min="0" step="0.01" defaultValue={dollars(value)} onBlur={(e) => onChange(toCents(e.target.value))}
          className="w-full bg-transparent px-1 py-2 text-sm outline-none" />
      </div>
    </label>
  );
}

function PackageCard({ pkg, schema, onSaved }) {
  const [p, setP] = useState(pkg);
  const [effective, setEffective] = useState("new_only");
  const [busy, setBusy] = useState(false);
  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const setPrice = (cycle, cents) => setP((x) => ({ ...x, pricing: { ...x.pricing, [cycle]: cents } }));
  const setPromo = (patch) => setP((x) => ({ ...x, promo: { ...(x.promo || {}), ...patch } }));
  const toggleFeat = (k) => setP((x) => ({ ...x, features: { ...x.features, [k]: !x.features?.[k] } }));
  const setLimit = (k, v) => setP((x) => ({ ...x, limits: { ...x.limits, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/match/admin/packages/${p.key}`, { ...p, price_effective: effective });
      toast.success(`Saved ${p.name}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => { try { await api.post(`/match/admin/packages/${p.key}/status`, { status }); toast.success(status); onSaved(); } catch (e) { toast.error(apiError(e)); } };
  const duplicate = async () => { try { await api.post(`/match/admin/packages/${p.key}/duplicate`); toast.success("Duplicated"); onSaved(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid={`match-pkg-${p.key}`} className="space-y-4 rounded-2xl border border-white/10 bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Heart size={18} weight="fill" className="text-rose-400" />
          <input value={p.name} onChange={(e) => set({ name: e.target.value })} className="rounded-lg bg-transparent text-lg font-semibold outline-none" data-testid={`match-name-${p.key}`} />
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-muted-foreground"}`}>{p.status}</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={p.badge || ""} onChange={(e) => set({ badge: e.target.value })} className="rounded-lg border border-white/10 bg-background px-2 py-1 text-xs">
            {(schema?.badges || [""]).map((b) => <option key={b} value={b}>{b || "No badge"}</option>)}
          </select>
          <button onClick={duplicate} className="rounded-full bg-secondary p-2 text-xs" title="Duplicate"><Copy size={14} /></button>
        </div>
      </div>

      <input value={p.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} placeholder="Tagline"
        className="w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none" />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(schema?.cycles || []).map((c) => <PriceInput key={c} label={c} value={p.pricing?.[c]} onChange={(v) => setPrice(c, v)} />)}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2"><span className="text-muted-foreground">Currency</span>
          <input value={p.currency || "USD"} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} className="w-20 rounded-lg border border-white/10 bg-background px-2 py-1" /></label>
        <label className="flex items-center gap-2"><span className="text-muted-foreground">Free trial (days)</span>
          <input type="number" min="0" defaultValue={p.trial_days || 0} onBlur={(e) => set({ trial_days: parseInt(e.target.value || 0, 10) })} className="w-16 rounded-lg border border-white/10 bg-background px-2 py-1" /></label>
      </div>

      <div className="rounded-xl border border-white/10 bg-background/50 p-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!p.promo?.enabled} onChange={(e) => setPromo({ enabled: e.target.checked })} /> Promotional / intro price (monthly)
        </label>
        {p.promo?.enabled && (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <PriceInput label="Promo $/mo" value={p.promo?.price_cents} onChange={(v) => setPromo({ price_cents: v })} />
            <label className="flex flex-col gap-1"><span className="text-[11px] uppercase text-muted-foreground">Label</span><input value={p.promo?.label || ""} onChange={(e) => setPromo({ label: e.target.value })} className="rounded-xl border border-white/10 bg-background px-2 py-2 text-sm" /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] uppercase text-muted-foreground">Starts</span><input type="date" onBlur={(e) => setPromo({ starts_at: e.target.value })} defaultValue={(p.promo?.starts_at || "").slice(0, 10)} className="rounded-xl border border-white/10 bg-background px-2 py-2 text-sm" /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] uppercase text-muted-foreground">Ends</span><input type="date" onBlur={(e) => setPromo({ ends_at: e.target.value })} defaultValue={(p.promo?.ends_at || "").slice(0, 10)} className="rounded-xl border border-white/10 bg-background px-2 py-2 text-sm" /></label>
          </div>
        )}
      </div>

      <details className="rounded-xl border border-white/10 bg-background/50 p-3">
        <summary className="cursor-pointer text-sm font-medium">Features &amp; limits</summary>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {(schema?.feature_keys || []).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!p.features?.[k]} onChange={() => toggleFeat(k)} /> {label}</label>
          ))}
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {(schema?.limit_keys || []).map(([k, label]) => (
            <label key={k} className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">{label}</span>
              <input value={p.limits?.[k] ?? ""} onChange={(e) => setLimit(k, e.target.value)} placeholder="number / unlimited / none"
                className="rounded-lg border border-white/10 bg-background px-2 py-1 text-xs" /></label>
          ))}
        </div>
      </details>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-3">
        <label className="flex items-center gap-2 text-xs">Apply price change to
          <select value={effective} onChange={(e) => setEffective(e.target.value)} className="rounded-lg border border-white/10 bg-background px-2 py-1">
            <option value="new_only">New customers only</option>
            <option value="next_renewal">All eligible at next renewal</option>
          </select>
        </label>
        <div className="flex items-center gap-2">
          {p.status === "active" ? <button onClick={() => setStatus("inactive")} className="rounded-full bg-secondary px-3 py-1.5 text-xs">Deactivate</button>
            : <button onClick={() => setStatus("active")} className="rounded-full bg-secondary px-3 py-1.5 text-xs">Activate</button>}
          <button onClick={() => setStatus("archived")} className="rounded-full bg-secondary px-3 py-1.5 text-xs">Archive</button>
          <button onClick={save} disabled={busy} data-testid={`match-save-${p.key}`} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"><FloppyDisk size={15} weight="bold" /> Save</button>
        </div>
      </div>
    </div>
  );
}

function BoostCard() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/match/admin/boost").then(({ data }) => setB(data.boost)).catch(() => {}); }, []);
  if (!b) return null;
  const setOpt = (i, field, val) => setB((x) => ({ ...x, options: x.options.map((o, j) => j === i ? { ...o, [field]: field === "cents" ? toCents(val) : parseInt(val || 0, 10) } : o) }));
  const save = async () => { try { await api.put("/match/admin/boost", b); toast.success("Boost saved"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div data-testid="match-boost" className="space-y-3 rounded-2xl border border-white/10 bg-card p-5">
      <h3 className="flex items-center gap-2 font-semibold"><Rocket size={18} weight="fill" className="text-amber-400" /> Profile Boost add-on</h3>
      <label className="flex items-center gap-2 text-sm">Boost duration (hours)
        <input type="number" min="1" defaultValue={b.duration_hours} onBlur={(e) => setB({ ...b, duration_hours: parseInt(e.target.value || 1, 10) })} className="w-20 rounded-lg border border-white/10 bg-background px-2 py-1" /></label>
      <div className="grid gap-2 sm:grid-cols-3">
        {(b.options || []).map((o, i) => (
          <div key={i} className="flex items-center gap-2 rounded-xl border border-white/10 bg-background p-2 text-sm">
            <input type="number" min="1" defaultValue={o.qty} onBlur={(e) => setOpt(i, "qty", e.target.value)} className="w-14 rounded bg-transparent" /> boosts ·
            <span>$</span><input type="number" min="0" step="0.01" defaultValue={dollars(o.cents)} onBlur={(e) => setOpt(i, "cents", e.target.value)} className="w-20 rounded bg-transparent" />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">{b.note}</p>
      <button onClick={save} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Save boost pricing</button>
    </div>
  );
}

export default function AdminMatchPackages() {
  const [schema, setSchema] = useState(null);
  const [packages, setPackages] = useState([]);
  const [audit, setAudit] = useState([]);
  const load = async () => {
    try {
      const [s, p, a] = await Promise.all([
        api.get("/match/admin/schema"), api.get("/match/admin/packages"), api.get("/match/admin/price-audit"),
      ]);
      setSchema(s.data); setPackages(p.data.packages); setAudit(a.data.audit);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-match-packages">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><Heart size={26} weight="fill" className="text-rose-400" /> iYeneh Match — Packages &amp; Pricing</h1>
        <p className="mt-1 text-sm text-muted-foreground">Launch prices are editable defaults — change price, cycles, promos, trials, features &amp; limits without redeploying. Safety features always stay free. Existing subscribers keep their contracted price.</p>
      </div>

      {packages.map((p) => <PackageCard key={p.key} pkg={p} schema={schema} onSaved={load} />)}

      <BoostCard />

      <div className="rounded-2xl border border-white/10 bg-card p-5">
        <h3 className="flex items-center gap-2 font-semibold"><ClockCounterClockwise size={18} className="text-sky-400" /> Price change audit</h3>
        {audit.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No price changes yet.</p> : (
          <div className="mt-3 space-y-1 text-xs">
            {audit.map((a, i) => (
              <div key={i} className="flex flex-wrap justify-between gap-2 border-b border-white/5 py-1">
                <span>{a.package_key} · {a.cycle}: ${dollars(a.old_cents)} → ${dollars(a.new_cents)}</span>
                <span className="text-muted-foreground">{a.changed_by} · {a.effective} · {(a.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
